import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SPLASH_PHASE, isSplashAwaitingGlideTarget, useSplashPhase, useSplashGlideLanded, useSplashGlideNear,
} from './splashHandoff';
import { SPLASH_GLIDE_ATTR, SPLASH_GLIDE_DEFAULT_MS, prefersReducedMotion } from './splashGlide';

// Never hidden longer than this past the splash starting to leave, whatever happens.
const REVEAL_BACKSTOP_EXTRA_MS = 400;

/**
 * useSplashGlideTarget(durationMs) — for the wordmark a splash glide should
 * land on (KAN-562). Spread `targetProps` on the element WRAPPING the
 * wordmark and apply `hiddenStyle` to it.
 *
 * It is a target only when a glide can still land on it: it mounted while
 * the splash was still up ('covered'), or while a leaving splash is still
 * looking for its target (`isSplashAwaitingGlideTarget`). Mounted after that
 * window, or with reduced motion on (never a glide), it is never marked or
 * hidden — the wordmark is simply there. One-shot: once revealed (the clone
 * landed, the glide was skipped, the splash cleared, or the backstop fired) it
 * drops the marker for good.
 */
export function useSplashGlideTarget(durationMs = SPLASH_GLIDE_DEFAULT_MS) {
  const [armed, setArmed] = useState(() => !prefersReducedMotion() && isSplashAwaitingGlideTarget());
  const phase = useSplashPhase();
  const landed = useSplashGlideLanded();
  const near = useSplashGlideNear();
  const elRef = useRef(null);
  const ref = useCallback((node) => { elRef.current = node; }, []);
  const landedAtMountRef = useRef(landed);

  // The clone landed on THIS element (a fresh report, not one from before mount).
  useEffect(() => {
    if (!armed || !landed || landed === landedAtMountRef.current) return;
    if (!elRef.current || landed.el === elRef.current) setArmed(false);
  }, [armed, landed]);

  // The splash is gone: any glide has landed by now (the overlay publishes
  // 'clear' no earlier than the landing) — reveal.
  useEffect(() => {
    if (armed && phase === SPLASH_PHASE.CLEAR) setArmed(false);
  }, [armed, phase]);

  // Backstop: the splash is leaving and nothing revealed us → reveal.
  useEffect(() => {
    if (!armed || phase === SPLASH_PHASE.COVERED) return undefined;
    const t = setTimeout(() => setArmed(false), durationMs + REVEAL_BACKSTOP_EXTRA_MS);
    return () => clearTimeout(t);
  }, [armed, phase, durationMs]);

  return {
    ref,
    targetProps: armed ? { [SPLASH_GLIDE_ATTR]: String(durationMs) } : {},
    hiddenStyle: armed ? { visibility: 'hidden' } : null,
    /** True while this element waits for the clone (or the splash) — the screen's timeline can key off it. */
    waiting: armed,
    /** True once the logo is ~88% of the way (or no glide runs / this element is no target) — start the screen's own entrance. */
    glideNear: !armed || near,
  };
}

export default useSplashGlideTarget;
